import { Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useIsJiraIntegrated } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import CollapsableStack from '@repo/common/components/CollapsableStack';
import { IconButton } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import {
	questionsQueryKeyFactory,
	useAuthUser,
	useIntegrationPrefetched,
	useUpdateQuestion,
	type IQuestion,
} from '../../api';
import {
	useExtendedUserList,
	useGuestUserList,
} from '../../api/hooks/user/useExtendedUserList';
import { SelectableProperty } from '../../components/EntityPageLayout/EntityPropertySidebar';
import { JiraIssueStack } from '../../components/EntityPageLayout/SidesheetStacks/Jira/JiraIssueStack';
import MetadataStack from '../../components/EntityPageLayout/SidesheetStacks/Metadata/MetadataStack';
import { RelatedResourceStack } from '../../components/EntityPageLayout/SidesheetStacks/RelatedResource/RelatedResourceStack';
import type { SidebarEntityKeys } from '../../components/EntityPageLayout/types';
import { getOwnerAndGroupSelectorOptions } from '../../components/EntityPageLayout/utils';
import { AssigneePropertySelector } from '../../components/Properties/AssigneePropertySelector';
import { CollectionsPropertySelector } from '../../components/Properties/CollectionsPropertySelector';
import { PriorityPropertySelector } from '../../components/Properties/PriorityPropertySelector';
import { PropertySelector } from '../../components/Properties/PropertySelector';
import { QuestionStatusPropertySelector } from '../../components/Properties/QuestionStatusPropertySelector';
import TagPropertySelector from '../../components/Properties/TagPropertySelector';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import { useCheckEntityUpdatePermission } from '../../utils/authorization/roles';
import { addToSubscribers } from '../../utils/subscriberHelpers';
import { CustomPropertiesController } from '../CustomProperties/CustomPropertiesController';

export const QuestionSidebarController = memo(
	({ question }: { question: IQuestion }) => {
		const { user, workspace, isGuestUser } = useAuthUser();
		const queryClient = useQueryClient();
		const editorOfEntity = useCheckEntityUpdatePermission({ entity: question });
		const { mutateAsync: updateQuestion } = useUpdateQuestion({});
		const { data: isJiraIntegrated } = useIsJiraIntegrated({});

		const { data: integration } = useIntegrationPrefetched({
			id: question.integration,
			options: {
				enabled: Boolean(question.integration),
			},
		});

		const handleEntityChange = useCallback(
			(key: SidebarEntityKeys) =>
				(value: DjangoValueType, saveRemotely: boolean = true) => {
					if (!saveRemotely) {
						queryClient.setQueryData(
							questionsQueryKeyFactory.byId(question.id),
							{
								...question,
								[key]: value,
							}
						);
						return;
					}

					updateQuestion({
						data: {
							id: question.id,
							[key]: value,
						},
					});
					trackEvent(
						`${question.entity_type}/${key}/update`,
						{ id: question.id },
						user,
						workspace
					);
				},
			[queryClient, question, updateQuestion, user, workspace]
		);

		const handleAssigneeChange = useCallback(
			(assigneeId: string) => {
				handleEntityChange('assigned_to')(assigneeId, true);

				// Optimistically add assignee to subscribers
				if (assigneeId) {
					const currentSubscribers = (question.subscribers ?? []) as string[];
					const updatedSubscribers = addToSubscribers(currentSubscribers, [
						assigneeId,
					]);
					handleEntityChange('subscribers')(updatedSubscribers, false);
				}
			},
			[handleEntityChange, question.subscribers]
		);

		const handleAssigneeGroupChange = useCallback(
			(groupId: string) => {
				handleEntityChange('assigned_to_group')(groupId, true);

				// Optimistically add assignee group to subscriber groups
				if (groupId) {
					const currentSubscriberGroups = (question.subscribers_groups ??
						[]) as string[];
					const updatedSubscriberGroups = addToSubscribers(
						currentSubscriberGroups,
						[groupId]
					);
					handleEntityChange('subscribers_groups')(
						updatedSubscriberGroups,
						false
					);
				}
			},
			[handleEntityChange, question.subscribers_groups]
		);

		const handleEntityChangeWithSubscribers = useCallback(
			(key: SidebarEntityKeys) =>
				(value: DjangoValueType, saveRemotely: boolean = true) => {
					if (key === 'assigned_to') {
						handleAssigneeChange(value as string);
						return;
					}

					if (key === 'assigned_to_group') {
						handleAssigneeGroupChange(value as string);
						return;
					}

					handleEntityChange(key)(value, saveRemotely);
				},
			[handleEntityChange, handleAssigneeChange, handleAssigneeGroupChange]
		);

		let source;
		if (question.native_type === 'Jira_question') {
			source = 'Jira';
		} else if (question.native_type === 'slack_thread') {
			source = 'Slack';
		} else {
			source = 'Secoda';
		}

		const { usersVisibleToGuests } = useGuestUserList();
		const { activeUsers, userGroups: groups } = useExtendedUserList();

		const subscriberOptions = useMemo(
			() =>
				getOwnerAndGroupSelectorOptions(
					(isGuestUser ? usersVisibleToGuests : activeUsers) ?? [],
					groups ?? [],
					handleEntityChange('subscribers'),
					handleEntityChange('subscribers_groups')
				).map((owner) => ({
					...owner,
					itemTypeName: 'subscribers',
				})),
			[
				isGuestUser,
				usersVisibleToGuests,
				activeUsers,
				groups,
				handleEntityChange,
			]
		);

		const subscribers = useMemo(
			() => [
				...(question.subscribers ?? []),
				...(question.subscribers_groups ?? []),
			],
			[question]
		);

		const validateSubscriberSelection = useCallback(
			(item: any, currentSelection: any[]) => {
				// Only apply validation for viewers
				if (user.role !== 'viewer') {
					return true;
				}

				// Allow viewers to add/remove themselves
				if (item.value === user.id) {
					return true;
				}

				// Show notification if viewer tries to add someone else
				showNotification({
					title: 'Cannot add subscriber',
					message: 'Users with the role Viewer can only subscribe themselves.',
					color: 'red',
				});

				return false;
			},
			[user.role, user.id]
		);

		return (
			<Stack>
				<CollapsableStack
					groupName="Properties"
					actions={
						editorOfEntity && (
							<Link to={'/settings/properties'}>
								<IconButton
									data-testid="add-custom-property-032487fh"
									iconName="plus"
									variant="tertiary"
									tooltip="Add property"
								/>
							</Link>
						)
					}
				>
					<Stack spacing="xs">
						<SelectableProperty label="Status">
							<QuestionStatusPropertySelector
								entity={question}
								viewerOfEntity={!editorOfEntity}
								onChange={handleEntityChange('status')}
							/>
						</SelectableProperty>
						<SelectableProperty label="Priority" dataTestId="priority">
							<PriorityPropertySelector
								entity={question}
								viewerOfEntity={!editorOfEntity}
								onChange={handleEntityChange('priority')}
							/>
						</SelectableProperty>
						<SelectableProperty label="Assigned to" dataTestId="assigned_to">
							<AssigneePropertySelector
								entity={question}
								viewerOfEntity={!editorOfEntity}
								handleEntityChange={handleEntityChangeWithSubscribers}
							/>
						</SelectableProperty>
						<SelectableProperty label="Tags" dataTestId="tags">
							<TagPropertySelector
								initialValue={question.tags}
								readOnly={!editorOfEntity}
								placeholder={!editorOfEntity ? 'No tags' : 'Add tag'}
								placeholderIconName="tag"
								forceVariant="tertiary"
								forceSourceTags={!!integration?.force_source_tags}
								entityIntegrationId={integration?.id}
								onChange={handleEntityChange('tags')}
							/>
						</SelectableProperty>
						<SelectableProperty label="Collections" dataTestId="collections">
							<CollectionsPropertySelector
								entity={question}
								viewerOfEntity={!editorOfEntity}
								onChange={handleEntityChange('collections')}
							/>
						</SelectableProperty>
						<CustomPropertiesController entity={question} />
					</Stack>
				</CollapsableStack>
				{isJiraIntegrated && (
					<JiraIssueStack question={question} isViewer={!editorOfEntity} />
				)}
				<RelatedResourceStack entity={question} isVisible />
				<MetadataStack
					integration={integration}
					entity={question}
					visibleUsers={
						(isGuestUser ? usersVisibleToGuests : activeUsers) ?? []
					}
					source={source}
				/>
				<CollapsableStack groupName="Subscribers">
					<Stack spacing="xs">
						<SelectableProperty label="Subscribers" dataTestId="subscribers">
							<PropertySelector
								selected={subscribers}
								type="multi"
								value="subscribers"
								iconType="avatar"
								options={subscriberOptions}
								permittedId={user.id}
								isViewerUser={false}
								readOnly={false}
								onValidateSelection={validateSubscriberSelection}
							/>
						</SelectableProperty>
					</Stack>
				</CollapsableStack>
			</Stack>
		);
	}
);
